import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import LegalTab from '../applicationFormTabs/LegalTab';
import { LegalInfoWithId } from '../../types/forms';
import api from '../../utils/api';

const LegalInformation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counsellorData, setCounsellorData] = useState<any>(null);
  const [legalInfo, setLegalInfo] = useState<LegalInfoWithId>({
    _id: '',
    dbsCheck: false,
    dbsType: undefined,
    dbsNumber: '',
    dbsIssueDate: undefined, // Issue/print date from certificate
    dbsExpiryDate: undefined, // Calculated from issue date (3 years)
    dbsCertificate: null,
    dbsUpdateServiceId: '',
    dbsUpdateServiceConsent: false,
    nationality: '',
    workPermit: false,
    workPermitDetails: '',
    workPermitExpiryDate: undefined,
    workPermitNoExpiry: false,
    insuranceCertificate: null,
    insuranceNumber: '',
    insuranceExpiryDate: undefined,
    insuranceProvider: '',
    insuranceCoverage: '',
    insuranceDetails: '',
    convictions: false,
    convictionsDetails: '',
    criminalInvestigation: false,
    criminalInvestigationDetails: '',
    // Added missing fields for Professional Credentials
    bacpNumber: '',
    supervisorName: '',
    supervisorPhoneNumber: '',
    // Added missing fields for Online Learning
    onlineLearningCourse: false,
    onlineLearningDetails: '',
    onlineLearningCertificate: null
  });
  const [saveStatus, setSaveStatus] = useState<'saved' | 'error' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchCounsellorData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/counsellors/${id}`);
      
      if (response.status === 200) {
        setCounsellorData(response.data);
        
        // If legal information already exists, populate the form
        if (response.data.application?.legal) {
          setLegalInfo(response.data.application.legal);
        }
      }
    } catch (err: any) {
      console.error('Error fetching counsellor data:', err);
      setError('Failed to load counsellor data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchCounsellorData();
    }
  }, [id, fetchCounsellorData]);

  const handleLegalInfoChange = (field: keyof LegalInfoWithId, value: any) => {
    setLegalInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateLegalInfo = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // DBS Check is MANDATORY for all counsellors
    if (!legalInfo.dbsCheck) {
      errors.push('DBS check is mandatory for all counsellors');
    }
    
    // If DBS check is selected, must choose a type
    if (legalInfo.dbsCheck && !legalInfo.dbsType) {
      errors.push('Please select DBS type (Certificate or Update Service)');
    }
    
    // DBS Certificate validation
    if (legalInfo.dbsCheck && legalInfo.dbsType === 'certificate') {
      if (!legalInfo.dbsNumber) errors.push('DBS number is required');
      if (!legalInfo.dbsIssueDate) errors.push('DBS issue date is required when Certificate is selected');
      if (!legalInfo.dbsCertificate) errors.push('DBS certificate file upload is required');
    }
    
    // DBS Update Service validation
    if (legalInfo.dbsCheck && legalInfo.dbsType === 'update_service') {
      if (!legalInfo.dbsNumber) errors.push('DBS number is required');
      if (!legalInfo.dbsUpdateServiceId) errors.push('DBS Update Service ID is required');
      if (!legalInfo.dbsUpdateServiceConsent) errors.push('You must consent to DBS Update Service checks');
    }
    
    // Nationality is mandatory
    if (!legalInfo.nationality) errors.push('Nationality is required');
    
    // Work permit conditional validation
    if (legalInfo.workPermit && !legalInfo.workPermitDetails) {
      errors.push('Work permit details are required when work permit is selected');
    }
    
    // Insurance is MANDATORY
    if (!legalInfo.insuranceProvider) errors.push('Insurance provider is required');
    if (!legalInfo.insuranceNumber) errors.push('Insurance policy number is required');
    if (!legalInfo.insuranceExpiryDate) errors.push('Insurance expiry date is required');
    if (!legalInfo.insuranceCertificate) errors.push('Insurance certificate file upload is required');
    
    // Professional Credentials are MANDATORY
    if (!legalInfo.bacpNumber) errors.push('BACP / NCPS number is required');
    if (!legalInfo.supervisorName) errors.push('Supervisor name is required');
    if (!legalInfo.supervisorPhoneNumber) errors.push('Supervisor phone number is required');
    
    // Convictions conditional validation
    if (legalInfo.convictions && !legalInfo.convictionsDetails) {
      errors.push('Conviction details are required when convictions are declared');
    }
    
    // Criminal investigation conditional validation
    if (legalInfo.criminalInvestigation && !legalInfo.criminalInvestigationDetails) {
      errors.push('Criminal investigation details are required when investigation is declared');
    }
    
    // Online learning conditional validation
    if (legalInfo.onlineLearningCourse) {
      if (!legalInfo.onlineLearningDetails) errors.push('Online learning details are required');
      if (!legalInfo.onlineLearningCertificate) errors.push('Online learning certificate file upload is required');
    }

    return { isValid: errors.length === 0, errors };
  };

  const handleSave = async () => {
    try {
      setSaveStatus(null);
      setError(null);
      
      const url = `/api/counsellors/${id}/legal-info`;
      const response = await api.put(url, legalInfo);
      
      if (response.status === 200) {
        setSaveStatus('saved');
        setSuccess('Your legal information has been saved successfully.');
      }
    } catch (err: any) {
      console.error('Error saving legal information:', err);
      setSaveStatus('error');
      setError(err.response?.data?.error || 'Error saving legal information');
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate all required fields
      const validation = validateLegalInfo();
      
      if (!validation.isValid) {
        setError('Please complete all required fields:\n' + validation.errors.join('\n'));
        return;
      }

      setSubmitting(true);
      setError(null);
      
      const url = `/api/counsellors/${id}/legal-info/submit`;
      
      const response = await api.post(url, legalInfo);

      if (response.status === 200) {
        setSuccess('Your legal information has been submitted successfully.');
        // Navigate back to the onboarding dashboard after a short delay
        setTimeout(() => {
          navigate('/counsellor/onboarding');
        }, 3000);
      }
    } catch (err: any) {
      console.error('Error submitting legal information:', err);
      setError(err.response?.data?.error || 'Error submitting legal information');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !counsellorData) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom align="center">
            Legal Information
          </Typography>
          <Typography variant="body1" paragraph>
            Please complete all required legal information to proceed with your onboarding process.
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <LegalTab
            formData={legalInfo}
            onChange={handleLegalInfoChange}
            onSave={handleSave}
            saveStatus={saveStatus}
            standalone={true}
          />

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/counsellor/onboarding')}
              disabled={submitting}
            >
              Back to Onboarding
            </Button>
            <Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSave}
                disabled={submitting}
                sx={{ mr: 2 }}
              >
                Save Progress
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Legal Information'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LegalInformation;
